import { useModals } from "ModalProvider";
import { Text } from "components/base/Form";
import Button from "components/base/Button";
import { useState } from "react";
import NotifyCustomerTemplateList from "./TemplateList";
import { IoIosSend } from "react-icons/io";
import { gql, useMutation, useQuery } from "@apollo/client";
import { formatDate } from "react-day-picker/moment";
import { Alert } from "components/base/Toast";

const NOTIFY_CUSTOMER = gql`
  mutation NOTIFY_CUSTOMER(
    $shipmentId: ID!
    $confirmedEmails: [String!]
    $content: String!
    $withPackingList: Boolean!
    $withCommercialInvoice: Boolean!
  ) {
    notifyCustomer(
      shipmentId: $shipmentId
      confirmedEmails: $confirmedEmails
      content: $content
      withPackingList: $withPackingList
      withCommercialInvoice: $withCommercialInvoice
    ) {
      message
    }
  }
`;

const SHIPMENT_PDF_DOCUMENTS = gql`
  query SHIPMENT_PDF_DOCUMENTS($shipmentId: ID!) {
    shipmentPdfDocuments(shipmentId: $shipmentId) {
      documentType
      filename
      creator
      created
    }
  }
`;

const NotifyCustomerView = ({
  shipmentId,
  initialNote = "",
  emails = [],
  invoiceNumber,
  customerName,
  hide,
}) => {
  const modal = useModals();
  const [content, setContent] = useState(initialNote);
  const [withPackingList, setWithPackingList] = useState(true);
  const [withCommercialInvoice, setWithCommercialInvoice] = useState(true);
  const {
    data,
    loading: documentsLoading,
    error: documentsError,
  } = useQuery(SHIPMENT_PDF_DOCUMENTS, {
    variables: { shipmentId },
    fetchPolicy: "network-only",
  });
  const [notifyCustomer, notifyCustomerRes] = useMutation(NOTIFY_CUSTOMER, {
    onCompleted() {
      Alert("success", "Email sent successfully.");
      hide();
    },
    onError(error) {
      Alert("error", error.message);
    },
  });
  const documentsByType = Object.fromEntries(
    (data?.shipmentPdfDocuments || []).map((item) => [item.documentType, item]),
  );
  const documents = [
    ["packing_list", "Packing List", withPackingList, setWithPackingList],
    [
      "commercial_invoice",
      "Commercial Invoice",
      withCommercialInvoice,
      setWithCommercialInvoice,
    ],
  ];
  const hasSelectedDocuments = documents
    .filter(([, , selected]) => selected)
    .every(([type]) => documentsByType[type]);

  function sendNotification() {
    notifyCustomer({
      variables: {
        shipmentId,
        confirmedEmails: emails,
        content,
        withPackingList,
        withCommercialInvoice,
      },
    });
  }

  function showTemplates() {
    modal.present({
      title: "Select a template",
      children: (
        <NotifyCustomerTemplateList
          shipmentId={shipmentId}
          onSelect={(template) => {
            const notes = template
              .replace("{customername}", customerName)
              .replace("{invoicenumber}", invoiceNumber);
            setContent(notes);
            modal.hide();
          }}
        />
      ),
    });
  }

  return (
    <div>
      <div>
        <div className="text-sm flex justify-between items-baseline">
          <div className="text-sm opacity-70">
            You are going to send a notification to the customer. Please write
            the content below.
          </div>
          <Button onClick={showTemplates}>Choose a template</Button>
        </div>
        <Text
          className="mt-2"
          rows={20}
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
        <div className="mt-2 ml-1 text-sm">
          Email will be sent to: {emails.join(", ")}
        </div>
        <div className="mt-2 space-y-1">
          {documents.map(([type, label, selected, setSelected]) => {
            const exportFile = documentsByType[type];
            const documentInfo = documentsError
              ? "Unable to load PDF documents."
              : documentsLoading
                ? "Loading latest PDF documents..."
                : exportFile
                  ? `${exportFile.filename} · ${exportFile.creator || "Unknown"} · ${formatDate(exportFile.created * 1000, "YYYY-MM-DD HH:mm")}`
                  : "Not generated";

            return (
              <div key={type} className="flex items-center">
                <input
                  type="checkbox"
                  id={`with-${type}`}
                  checked={selected}
                  onChange={(e) => setSelected(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor={`with-${type}`} className="text-sm">
                  Attach {label}
                </label>
                <span className="ml-3 text-xs text-orange-500">
                  {documentInfo}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          primary
          icon={<IoIosSend />}
          onClick={sendNotification}
          loading={notifyCustomerRes.loading}
          disabled={
            notifyCustomerRes.loading ||
            ((withPackingList || withCommercialInvoice) &&
              (documentsLoading || documentsError || !hasSelectedDocuments))
          }
        >
          Send Notification
        </Button>
      </div>
    </div>
  );
};

export default NotifyCustomerView;
